import React from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useSubmitEvidence } from '../../hooks/useRequirements';
import { useTenant } from '../../contexts/TenantContext';
import { Upload, FileText, X } from 'lucide-react';
import toast from 'react-hot-toast';

interface EvidenceUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  requirementId: string;
  requirementName: string;
}

export const EvidenceUploadModal: React.FC<EvidenceUploadModalProps> = ({
  isOpen,
  onClose,
  requirementId,
  requirementName
}) => {
  const { node } = useTenant();
  const [file, setFile] = React.useState<File | null>(null);
  const [notes, setNotes] = React.useState('');
  const submitMutation = useSubmitEvidence(node?.id || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    submitMutation.mutate({ requirementId, file, notes }, {
      onSuccess: () => {
        toast.success('Evidence submitted successfully');
        onClose();
        setFile(null);
        setNotes('');
      },
      onError: () => {
        toast.error('Failed to submit evidence');
      }
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Submit Evidence"
      className="max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest font-mono mb-2">Requirement</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{requirementName}</p>
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-semibold text-gray-700">File Attachment</label>
          <div className="relative">
            {!file ? (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer dark:bg-slate-950">
                <Upload size={24} className="text-gray-400 mb-2" />
                <span className="text-xs text-gray-500">Click to upload or drag and drop</span>
                <span className="text-[10px] text-gray-400 mt-1">PDF, JPG, PNG, DOC (Max 10MB)</span>
                <input 
                  type="file" 
                  className="hidden" 
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                />
              </label>
            ) : (
              <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg text-blue-600 shadow-sm dark:bg-slate-900">
                    <FileText size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate max-w-[200px] dark:text-white">{file.name}</p>
                    <p className="text-[10px] text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                </div>
                <button 
                  type="button" 
                  onClick={() => setFile(null)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-semibold text-gray-700">Notes (Optional)</label>
          <textarea
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
            rows={4}
            placeholder="Add any context or details about your submission..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button variant="secondary" className="flex-1" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button 
            variant="primary" 
            className="flex-1" 
            type="submit" 
            isLoading={submitMutation.isPending}
            disabled={!file}
          >
            Submit
          </Button>
        </div>
      </form>
    </Modal>
  );
};
